import { FC, useContext } from 'react';

import { Button, CloseIcon } from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { useRedeemBooking, useSendShellCommand } from 'hooks';
import { LocalizationContext } from 'localization';
import { getUniqueSortedBookings } from 'utils';

import { openConfirmDialog } from '../../services/Confirmation';
import { IQuickMenu } from './interfaces';
import s from './QuickMenu.module.scss';

export const QuickMenu: FC<IQuickMenu> = ({
  hosts,
  onClose,
  selectedItems,
  openStartSeanceModal,
  openBookingModal,
  openSendMessageModal,
  mod,
}) => {
  const { t } = useContext(LocalizationContext);
  const selectedHosts = hosts?.filter((host) =>
    selectedItems.has(host.id.toString())
  );
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const selectedHostsBooking = selectedHosts.filter(
    (host) => host.bookings.length > 0
  );

  const isOnlyOnlineBooking = selectedHostsBooking.every(
    (host) => host.bookings?.[0]?.byClient
  );

  const redeemBooking = useRedeemBooking();
  const { sendCommand, sendCommands } = useSendShellCommand();

  return (
    <div className={cn(s.container, mod && s.mod)} data-name="quick-menu">
      <div className={cn(s.count, s.text)}>
        {t('hostMap.quickMenu.host.title', selectedItems.size)}
      </div>
      <ul className={s.actions}>
        {permissions?.has('payment_create') &&
          selectedHosts?.every((host) => !host.in_service) &&
          selectedHosts?.every((host) => !host.locked) &&
          selectedHosts?.every(
            (host) => !host.client_sessions?.[0]?.postpaid
          ) &&
          selectedHosts?.every(
            (host) => host.group_id === selectedHosts[0].group_id
          ) &&
          selectedHosts.every((host) => !host.user) && (
            <li className={s.action}>
              <Button
                variant="secondary"
                iconName="ClockIcon"
                className={s.button}
                onClick={() => openStartSeanceModal()}
              >
                {t('host.ctx.tariff')}
              </Button>
            </li>
          )}
        {permissions?.has('booking_manage') && (
          <li className={s.action}>
            <Button
              variant="secondary"
              className={s.button}
              iconName="CirclePlusIcon"
              onClick={() => openBookingModal()}
            >
              {t('bookings.add.submit')}
            </Button>
          </li>
        )}
        {permissions?.has('booking_manage') &&
          selectedHostsBooking.length !== 0 &&
          !isOnlyOnlineBooking && (
            <li className={s.action}>
              <Button
                variant="secondary"
                className={s.button}
                iconName="CheckIcon"
                disabled={isOnlyOnlineBooking}
                onClick={() => {
                  const uniqueDialogData = getUniqueSortedBookings(
                    selectedHosts,
                    hosts
                  );

                  openConfirmDialog(t, 'booking', {
                    title: t('modal.confirm.booking.title'),
                    acceptTitle: t('confirm'),
                    declineTitle: t('cancel_2'),
                    dialogData: uniqueDialogData ?? [],
                  }).then(({ status }) => {
                    if (status === 'ACCEPTED') {
                      selectedHosts?.forEach((host) => {
                        if (
                          host.bookings.length &&
                          !host.bookings[0].byClient
                        ) {
                          const nearestBooking = host.bookings[0];
                          return redeemBooking({
                            variables: { id: nearestBooking.id },
                          });
                        }
                      });
                    }
                  });
                }}
              >
                {t('host.ctx.bookings.confirm')}
              </Button>
            </li>
          )}
        {permissions?.has('host_command_manage') && (
          <li className={s.action}>
            <Button
              variant="secondary"
              iconName="MessageIcon"
              className={s.button}
              onClick={() => openSendMessageModal()}
            >
              {t('host.ctx.notify')}
            </Button>
          </li>
        )}

        {permissions?.has('host_command_manage') &&
          selectedHosts.every((host) => !host.online) && (
            <li className={s.action}>
              <Button
                variant="secondary"
                className={s.button}
                iconName="ShutdownIcon"
                onClick={() => {
                  const arr = Array.from(selectedItems).map((item) =>
                    Number(item)
                  );

                  if (selectedItems.size === 1) {
                    return sendCommand({ host_id: arr[0], action: 'WOL' });
                  } else {
                    return sendCommands({ host_ids: arr, action: 'WOL' });
                  }
                }}
              >
                {t('host.ctx.on')}
              </Button>
            </li>
          )}
      </ul>
      <button onClick={() => onClose(new Set())} className={s.close}>
        <CloseIcon size="m" color="link-info" />
      </button>
    </div>
  );
};
